import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowUpRight, CalendarDays, Check, ChevronRight, Clock3, Lock, ShieldCheck, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { card, badge, useApi, Skeleton, Stagger, Item } from "@/components/portal/ui";

function Ring({ percent }) {
  const c = 97.4;
  return (
    <div className="relative w-20 h-20" data-testid="grow-percent">
      <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="3.5" />
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#10b981" strokeWidth="3.5" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - percent / 100)} style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(.22,1,.36,1)" }} />
      </svg>
      <span className="absolute inset-0 grid place-items-center font-black text-lg">{percent}%</span>
    </div>
  );
}

function ClassCard({ c, i }) {
  const done = c.completed >= c.sessions;
  const pct = Math.round((c.completed / (c.sessions || 1)) * 100);
  const [tone, label] = c.locked ? ["warn", "Terkunci"] : done ? ["success", "Selesai"] : ["info", `Berjalan · ${pct}%`];
  return (
    <div className={`${card} ${c.locked ? "opacity-80" : ""}`} data-testid={`grow-class-${c.code}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[11px] font-bold rounded-full px-3 py-1 inline-flex items-center gap-1 ${badge[tone]}`}>{c.locked && <Lock className="w-3 h-3" />}{label}</span>
        <span className="text-xs text-slate-500">Tahap {i + 1} · Kelas {c.code}</span>
      </div>
      <p className="mt-3 font-extrabold text-slate-900">{c.title}</p>
      <p className="text-xs text-slate-500 mt-1">{c.description}</p>
      <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${done ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${pct}%`, transition: "width 1.2s cubic-bezier(.22,1,.36,1)" }} /></div>
      <p className="mt-2 text-xs text-slate-500">{c.completed} / {c.sessions} {c.courses?.length ? "course selesai" : "sesi"}</p>

      {c.locked ? (
        <p className="mt-4 text-xs font-bold text-amber-700 bg-amber-50 rounded-xl px-3 py-2" data-testid={`grow-lock-note-${c.code}`}>Selesaikan tahap sebelumnya (video, tugas & ujian) untuk membuka tahap ini.</p>
      ) : c.courses?.length ? (
        <div className="mt-4 space-y-2" data-testid={`grow-courses-${c.code}`}>
          {c.courses.map((co) => (
            <Link key={co.id} to={`/portal/elearning/${co.id}`} data-testid={`grow-course-link-${co.id}`}
              className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 hover:border-blue-300 active:scale-[0.99] transition">
              <span className={`w-7 h-7 rounded-lg grid place-items-center shrink-0 ${co.completed ? "bg-emerald-500 text-white" : "bg-blue-50 text-blue-600"}`}>{co.completed ? <Check className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}</span>
              <span className="flex-1 text-sm font-bold text-slate-800 truncate">{co.title}</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2">Materi tahap ini akan dibuka saat course E-Learning-nya diterbitkan OPC.</p>
      )}
    </div>
  );
}

function TicketQR({ item }) {
  return (
    <div className="mt-3 rounded-2xl bg-slate-900 text-white p-4 flex items-center gap-4" data-testid={`tts-ticket-${item.id}`}>
      <span className="bg-white p-2 rounded-xl shrink-0"><QRCodeSVG value={`GIM-TTS:${item.ticket_code}`} size={78} /></span>
      <div className="min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">Tiket TTS</p>
        <p className="mt-1 text-sm font-bold truncate">{item.name}</p>
        <p className="text-[11px] text-white/50 break-all">{item.ticket_code}</p>
        {item.scanned_at && <p className="mt-1 text-[10px] font-bold text-emerald-300">Sudah discan di lokasi</p>}
      </div>
    </div>
  );
}

function CommunityCard({ k, mine, onApply, onRelease, busy }) {
  const status = k.my_status;
  const pending = status === "pending";
  const approved = k.type === "tts" ? status === "approved" : mine;
  return (
    <div className={`rounded-3xl p-5 ${approved ? "bg-slate-900 text-white" : "bg-white border border-slate-100 shadow-sm"}`} data-testid={`komsel-${k.id}`}>
      <div className="flex items-center gap-4">
        <span className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${approved ? "bg-white/10" : k.type === "tts" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}><Users className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate">{k.name}</p>
          <p className={`text-xs truncate ${approved ? "text-white/60" : "text-slate-500"}`}>{[k.category, k.day, k.time, k.leader].filter(Boolean).join(" · ")}</p>
        </div>
        {approved ? <span className="text-[11px] font-bold bg-emerald-500 text-white rounded-full px-3 py-1 flex items-center gap-1 shrink-0"><Check className="w-3 h-3" />{k.type === "tts" ? "Terdaftar" : "RC-ku"}</span>
          : pending ? <span className="text-[11px] font-bold bg-amber-100 text-amber-700 rounded-full px-3 py-1 flex items-center gap-1 shrink-0"><Clock3 className="w-3 h-3" />Menunggu</span>
          : <button onClick={() => onApply(k)} disabled={busy} data-testid={`komsel-join-${k.id}`} className="shrink-0 text-xs font-bold text-blue-600 bg-blue-50 rounded-full px-3 py-2 hover:bg-blue-100 active:scale-95 transition flex items-center gap-1">{k.type === "tts" ? "Daftar" : "Gabung"} <ArrowUpRight className="w-3 h-3" /></button>}
      </div>
      {pending && <p className={`mt-3 text-[11px] font-semibold ${approved ? "text-white/60" : "text-amber-700"}`}>Permintaanmu sedang ditinjau leader {k.leader || "komunitas"}.</p>}
      {approved && k.type === "tts" && k.ticket_code && <TicketQR item={{ id: k.id, name: k.name, ticket_code: k.ticket_code, scanned_at: k.scanned_at }} />}
      {approved && k.type === "rc" && (
        <button onClick={() => onRelease(k)} disabled={busy} data-testid="rc-release-button" className="mt-4 w-full text-xs font-bold bg-white/10 border border-white/20 rounded-xl py-2.5 hover:bg-white/15 transition">Ajukan Pindah RC (lepas keanggotaan)</button>
      )}
    </div>
  );
}

function MeetingCard({ m, onRegister, busy }) {
  return (
    <div className={`rounded-3xl p-5 ${m.ticket_code ? "bg-slate-900 text-white" : "bg-white border border-slate-100 shadow-sm"}`} data-testid={`meeting-${m.id}`}>
      <div className="flex items-center gap-4">
        <span className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${m.ticket_code ? "bg-white/10" : "bg-indigo-100 text-indigo-700"}`}><CalendarDays className="w-5 h-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate">{m.title}</p>
          <p className={`text-xs truncate ${m.ticket_code ? "text-white/60" : "text-slate-500"}`}>{[m.community_name, m.date, m.time, m.location].filter(Boolean).join(" · ")}</p>
        </div>
        {m.ticket_code
          ? <span className="text-[11px] font-bold bg-emerald-500 text-white rounded-full px-3 py-1 flex items-center gap-1 shrink-0"><Check className="w-3 h-3" />Terdaftar</span>
          : <button onClick={() => onRegister(m)} disabled={busy} data-testid={`meeting-register-${m.id}`} className="shrink-0 text-xs font-bold text-indigo-600 bg-indigo-50 rounded-full px-3 py-2 hover:bg-indigo-100 active:scale-95 transition flex items-center gap-1">Daftar <ArrowUpRight className="w-3 h-3" /></button>}
      </div>
      {m.ticket_code && (
        <div className="mt-3 rounded-2xl bg-white/10 p-4 flex items-center gap-4" data-testid={`meeting-ticket-${m.id}`}>
          <span className="bg-white p-2 rounded-xl shrink-0"><QRCodeSVG value={`GIM-MTG:${m.ticket_code}`} size={78} /></span>
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">Tiket Kegiatan</p>
            <p className="mt-1 text-sm font-bold truncate">{m.title}</p>
            <p className="text-[11px] text-white/50 break-all">{m.ticket_code}</p>
            {m.scanned_at && <p className="mt-1 text-[10px] font-bold text-emerald-300">Kehadiran tercatat</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Grow() {
  const { setUser } = useAuth();
  const { data, reload } = useApi("/portal/grow");
  const { data: comm, reload: reloadComm } = useApi("/portal/communities");
  const { data: mtg, reload: reloadMtg } = useApi("/portal/communities/meetings");
  const [busy, setBusy] = useState(false);

  const registerMeeting = async (m) => {
    setBusy(true);
    try {
      await api.post(`/portal/communities/meetings/${m.id}/register`);
      toast.success("Terdaftar! Tiket QR-mu sudah siap");
      reloadMtg();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async (k) => {
    setBusy(true);
    try {
      await api.post(`/portal/communities/${k.id}/apply`);
      toast.success(`Permintaan dikirim ke leader ${k.name}`);
      reloadComm();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const release = async () => {
    setBusy(true);
    try {
      await api.post("/portal/communities/rc/release");
      toast.success("Permintaan pindah RC dikirim ke leader");
      reloadComm();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { if (comm) setUser((u) => ({ ...u, komsel_id: comm.my_rc })); }, [comm, setUser]);

  const all = comm?.communities || [];
  const rc = all.filter((k) => k.type !== "tts");
  const tts = all.filter((k) => k.type === "tts");

  return (
    <div data-testid="portal-grow">
      <div className="relative overflow-hidden rounded-b-[2.5rem] bg-gradient-to-br from-indigo-600 via-blue-900 to-slate-900 text-white px-6 pt-12 pb-10">
        <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-white/10" />
        <p className="relative text-xs font-bold uppercase tracking-[0.2em] text-blue-200">Grow Track</p>
        <h1 className="relative text-3xl font-extrabold tracking-tight mt-1">Bertumbuh</h1>
        <div className="relative mt-6 flex items-center gap-5">
          <Ring percent={data?.percent || 0} />
          <div><p className="font-bold">Perjalanan Murid</p><p className="text-sm text-white/60">{data ? `${data.classes_done} dari ${data.classes.length} tahap selesai` : "Memuat…"}</p></div>
        </div>
      </div>
      <div className="px-5 mt-6">
        {!data ? <Skeleton className="h-64" /> : (
          <Stagger className="space-y-4">
            {data.classes.map((c, i) => <Item key={c.id}><ClassCard c={c} i={i} /></Item>)}
            {comm?.is_leader && (
              <Item>
                <Link to="/portal/community" data-testid="community-leader-link" className="block rounded-3xl bg-amber-400 text-slate-900 p-5 active:scale-[0.99] transition">
                  <ShieldCheck className="w-6 h-6" />
                  <p className="mt-3 font-extrabold">Kelola Komunitas</p>
                  <p className="text-xs text-slate-800/70 mt-0.5">Setujui pendaftaran RC/TTS & pindai tiket peserta di lokasi acara.</p>
                </Link>
              </Item>
            )}
            {mtg?.meetings?.length > 0 && (
              <>
                <Item><p className="px-1 pt-4 text-sm font-bold text-slate-900">Kegiatan Komunitasku</p></Item>
                {mtg.meetings.map((m) => <Item key={m.id}><MeetingCard m={m} onRegister={registerMeeting} busy={busy} /></Item>)}
              </>
            )}
            <Item><p className="px-1 pt-4 text-sm font-bold text-slate-900">Relationship Community (RC) · maksimal 1</p></Item>
            {rc.map((k) => <Item key={k.id}><CommunityCard k={k} mine={comm.my_rc === k.id} onApply={apply} onRelease={release} busy={busy} /></Item>)}
            {rc.length === 0 && <Item><p className={`${card} text-center text-sm text-slate-500`} data-testid="rc-empty">Belum ada Relationship Community terdaftar.</p></Item>}
            <Item><p className="px-1 pt-4 text-sm font-bold text-slate-900">TTS Community · Time To Shine</p></Item>
            {tts.map((k) => <Item key={k.id}><CommunityCard k={k} onApply={apply} onRelease={release} busy={busy} /></Item>)}
            {tts.length === 0 && <Item><p className={`${card} text-center text-sm text-slate-500`} data-testid="tts-empty">Belum ada kegiatan TTS Community. Nantikan pengumuman berikutnya!</p></Item>}
          </Stagger>
        )}
      </div>
    </div>
  );
}
